// ============================================
// WGSL struct layout — offsets/sizes/stride from Slang type reflection
// ============================================
//
// Computes WGSL memory layout for struct types described by Slang reflection JSON. Two uses:
//  - buffer element strides: the reflection gives per-field offsets (std430) but NOT the total
//    element stride, which dispatch/createBuffer sizing needs;
//  - the inspector's packed-layout visualization (and a cross-check of reflected offsets).
//
// Rules implemented (WGSL spec §memory layouts): scalars 4/4 (f16 2/2), vec2 8/8, vec3 12/16,
// vec4 16/16 (halved for f16), matCxR = array of C columns of vecR (column-stride = roundUp of the
// column vector), struct align = max member align, struct size = roundUp(align, last offset + size).
// `uniform` address space additionally rounds struct/array alignment up to 16 (std140-style).

import type { SlangComputeType, ComputeStructLayout, ComputeFieldLayout } from './types';

export type LayoutSpace = 'storage' | 'uniform';

interface TypeLayout {
  size: number;
  align: number;
}

const roundUp = (align: number, n: number): number => Math.ceil(n / align) * align;

function scalarLayout(scalarType: string | undefined): TypeLayout {
  return scalarType === 'float16' ? { size: 2, align: 2 } : { size: 4, align: 4 };
}

function vectorLayout(count: number, elem: TypeLayout): TypeLayout {
  // vec3 aligns like vec4; vec2 like two scalars.
  const align = (count === 3 ? 4 : count) * elem.size;
  return { size: count * elem.size, align };
}

/** Size + alignment of a Slang-reflected type in the given address space. Unknown kinds → 4/4. */
export function typeLayoutOf(type: SlangComputeType | undefined, space: LayoutSpace = 'storage'): TypeLayout {
  if (!type) return { size: 4, align: 4 };
  switch (type.kind) {
    case 'scalar':
      return scalarLayout(type.scalarType);
    case 'vector': {
      return vectorLayout(type.elementCount ?? 4, scalarLayout(type.elementType?.scalarType));
    }
    case 'matrix': {
      // matCxR: C columns of vecR; column stride = roundUp(align(vecR), size(vecR)).
      const col = vectorLayout(type.rowCount ?? 4, scalarLayout(type.elementType?.scalarType));
      const stride = roundUp(col.align, col.size);
      const cols = type.columnCount ?? 4;
      const align = space === 'uniform' ? Math.max(col.align, 16) : col.align;
      return { size: stride * cols, align };
    }
    case 'array': {
      const elem = typeLayoutOf(type.elementType, space);
      const align = space === 'uniform' ? Math.max(elem.align, 16) : elem.align;
      const stride = roundUp(align, elem.size);
      return { size: stride * (type.elementCount ?? 1), align };
    }
    case 'struct': {
      const l = computeStructLayout(type, space);
      const align = structAlign(type, space);
      return { size: l.stride, align };
    }
    default:
      return { size: 4, align: 4 };
  }
}

function structAlign(type: SlangComputeType, space: LayoutSpace): number {
  let align = 1;
  for (const f of type.fields ?? []) align = Math.max(align, typeLayoutOf(f.type, space).align);
  if (space === 'uniform') align = Math.max(align, 16);
  return align;
}

/** Human display label for a Slang-reflected type ('float3', 'uint', 'Particle', ...). */
export function typeLabelOf(type: SlangComputeType | undefined): string {
  if (!type) return '?';
  switch (type.kind) {
    case 'scalar':
      switch (type.scalarType) {
        case 'float32': return 'float';
        case 'float16': return 'half';
        case 'int32': return 'int';
        case 'uint32': return 'uint';
        case 'bool': return 'bool';
        default: return type.scalarType ?? 'scalar';
      }
    case 'vector':
      return `${typeLabelOf(type.elementType)}${type.elementCount ?? ''}`;
    case 'matrix':
      return `${typeLabelOf(type.elementType)}${type.columnCount ?? 4}x${type.rowCount ?? 4}`;
    case 'array':
      return `${typeLabelOf(type.elementType)}[${type.elementCount ?? ''}]`;
    case 'struct':
      return type.name ?? 'struct';
    default:
      return type.name ?? type.kind;
  }
}

/**
 * Full field layout (offsets/sizes) + total stride of a reflected struct type. When the reflection
 * already carries per-field byte offsets (`fields[].binding {kind:'uniform', offset}` — slangc emits
 * them for buffer element structs), those are AUTHORITATIVE and only the stride is computed;
 * otherwise offsets are computed from the WGSL rules above.
 */
export function computeStructLayout(type: SlangComputeType, space: LayoutSpace = 'storage'): ComputeStructLayout {
  const fields: ComputeFieldLayout[] = [];
  let offset = 0;
  let maxEnd = 0;
  for (const f of type.fields ?? []) {
    const l = typeLayoutOf(f.type, space);
    const reflected = f.binding?.kind === 'uniform' ? f.binding.offset : undefined;
    const fieldOffset = reflected ?? roundUp(l.align, offset);
    const size = (f.binding?.kind === 'uniform' ? f.binding.size : undefined) ?? l.size;
    fields.push({ name: f.name, type: typeLabelOf(f.type), offset: fieldOffset, size });
    offset = fieldOffset + size;
    maxEnd = Math.max(maxEnd, fieldOffset + size);
  }
  const align = structAlign(type, space);
  return { name: type.name ?? 'struct', stride: roundUp(align, Math.max(maxEnd, 1)), fields };
}
